import React from 'react';
import { SpaceBetween, Alert, Button, Spinner, Modal } from '@amzn/awsui-components-core';
import { DocumentUploader } from '../../components/upload/DocumentUploader';
import { EAHuddleService } from 'src/services/EAHuddleService';

interface UploadTemplateContainerProps {
  visible: boolean;
  onClose: () => void;
  isProcessingDocument: boolean;
  documentError: string;
  onFileSelected: (file: File) => void;
  onFormattedContentExtracted: (htmlContent: string) => void;
}

export const UploadTemplateContainer: React.FC<UploadTemplateContainerProps> = ({
  visible,
  onClose,
  isProcessingDocument,
  documentError,
  onFileSelected,
  onFormattedContentExtracted
}) => {
  // Wrapper function to handle file selection - only close modal on successful completion
  const handleFileSelected = (file: File) => {
    onFileSelected(file);
    // Don't close modal immediately - let processing complete
    // Modal will be closed by parent component after successful processing
  };

  // Handle modal close
  const handleModalClose = () => {
    onClose();
  };

  return (
    <Modal visible={visible} onDismiss={handleModalClose} header="Upload Template" size="medium">
      <SpaceBetween direction="vertical" size="s">
        <>
          <div>
            <p>Upload your template to have EA Huddle automatically populate your meeting attendees and subject line</p>
          </div>

          <SpaceBetween direction="vertical" size="s">
            <Button variant="inline-link" onClick={() => EAHuddleService.downloadTemplate()}>
              📄 Download Sample Template
            </Button>

            <p>
              (optional) EA huddle can read template of various formats; if you already have a template in Word format, don't worry about the template
              and upload your file directly!
            </p>

            <DocumentUploader
              onFileSelected={handleFileSelected}
              onFormattedContentExtracted={onFormattedContentExtracted}
              isProcessing={isProcessingDocument}
              error={documentError}
              shouldRenderPreview={true}
            />
          </SpaceBetween>

          {/* Document Processing Loading Alert */}
          {isProcessingDocument && (
            <Alert statusIconAriaLabel="Loading" type="info" header="Processing Document...">
              <SpaceBetween direction="horizontal" size="s" alignItems="center">
                <Spinner size="normal" />
                <span>Analyzing document content and resolving attendee names. This may take upto 30 seconds, to cancel refresh the page.</span>
              </SpaceBetween>
            </Alert>
          )}

          {documentError && (
            <Alert statusIconAriaLabel="Error" type="error" header="Document Processing Error">
              Please retry uploading the file using the "Choose File" button above.
              <br />
              <br />
              If the problem persists, please{' '}
              <a href="https://t.corp.amazon.com/create/templates/8d963482-2c95-4b8f-bdbd-b9dbc1db0bc0" target="_blank" rel="noopener noreferrer">
                create a ticket
              </a>
              .
            </Alert>
          )}
        </>
      </SpaceBetween>
    </Modal>
  );
};
